import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { LoadingButton } from "../../../components/Button/LoadingButton"; // or replace with a button if needed

import {
  apiPing,
  apiStartService,
  apiStopService
} from "../../../common"; // adjust path if needed

import { setAgentConnected, setGraphList, setSelectedGraphId, setOptions } from "../../../store/reducers/global";
import { RootState } from "../../../redux/store";
import { rtcManager } from "../../../manager"; // now that it's CRA, static import is fine

let intervalId: ReturnType<typeof setInterval> | null = null;

export const AgentPage: React.FC = () => {
  const agentConnected = useSelector((state: RootState) => state.global.agentConnected);

  const channel = useSelector((state: RootState) => state.global.options.channel);
  const userId = useSelector((state: RootState) => state.global.options.userId);
  const language = useSelector((state: RootState) => state.global.language);
  const voiceType = useSelector((state: RootState) => state.global.voiceType);
  const graphName = useSelector((state: RootState) => state.global.selectedGraphId);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [channelInput, setChannelInput] = useState("");
  const [userIdInput, setUserIdInput] = useState("1001");

  useEffect(() => {
    dispatch(setGraphList(["voice_assistant"]));
    dispatch(setSelectedGraphId("voice_assistant"));
  }, [dispatch]);

  useEffect(() => {
    if (channel) {
      startPing();
      checkAgentConnected();
    }
  }, [channel]);

  useEffect(() => {
    const requestMicPermission = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log("Microphone permission granted");
      } catch (err) {
        console.warn("Mic permission error", err);
        toast("Please allow microphone access.");
      }
    };
    requestMicPermission();
  }, []);

  const checkAgentConnected = async () => {
    if (!channel) return;
    const res = await apiPing(channel);
    if (res?.code === 0) {
      dispatch(setAgentConnected(true));
    }
  };

  const startPing = () => {
    if (intervalId) clearInterval(intervalId);
    intervalId = setInterval(() => {
      apiPing(channel);
    }, 3000);
  };

  const stopPing = () => {
    if (intervalId) clearInterval(intervalId);
    intervalId = null;
  };

  const onClickConnect = async () => {
    if (loading) return;
   
    if (!channel && channelInput) {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        toast("Microphone access is required.");
        return;
      }

      dispatch(setOptions({
        channel: channelInput,
        userId: parseInt(userIdInput),
      }));
      return;
    }

    setLoading(true);

    if (agentConnected) {
      await apiStopService(channel);
      dispatch(setAgentConnected(false));
      toast("Agent disconnected");
      stopPing();
      await rtcManager.destroy();
    } else {
      try {
        await rtcManager.join({ channel, userId });
        await rtcManager.createMicrophoneAudioTrack();
        await rtcManager.publish();

        const res = await apiStartService({
          channel,
          userId,
          graphName,
          language,
          voiceType
        });

        const { code, msg } = res || {};
        if (code !== 0) {
          if (code === "10001") {
            toast("Usage limit exceeded. Try again later.");
          } else {
            toast(`Error: ${msg}`);
          }
          throw new Error(msg);
        }

        dispatch(setAgentConnected(true));
        toast("Agent connected");
        startPing();
      } catch (error) {
        console.error("Agent connection error", error);
        toast("Failed to connect to agent.");
        await rtcManager.destroy();
      }
    }

    setLoading(false);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-100">
      <h1 className="text-2xl font-bold mb-8">Voice24 Agent</h1>

      <div className="w-full max-w-md bg-white p-6 rounded-lg shadow-md">
        {!channel ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Channel:</label>
              <input
                type="text"
                value={channelInput}
                onChange={(e) => setChannelInput(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Enter channel name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">User ID:</label>
              <input
                type="text"
                value={userIdInput}
                onChange={(e) => setUserIdInput(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Enter user ID"
              />
            </div>
          </div>
        ) : (
          <div className="mb-4 space-y-1 text-sm">
            <div>
              Channel: <strong>{channel}</strong>
            </div>
            <div>
              User ID: <strong>{userId}</strong>
            </div>
            <div>
              Graph: <strong>{graphName}</strong>
            </div>
            <div>
              Status:{" "}
              <strong className={agentConnected ? "text-green-600" : "text-red-600"}>
                {agentConnected ? "Connected" : "Disconnected"}
              </strong>
            </div>
          </div>
        )}

        <div className="mt-6">
          <LoadingButton
            onClick={onClickConnect}
            variant={!agentConnected ? "default" : "destructive"}
            disabled={(!channel && !channelInput) || !graphName}
            loading={loading}
            className="w-full"
          >
            {loading
              ? "Connecting"
              : !channel
              ? "Set Channel"
              : agentConnected
              ? "Disconnect"
              : "Connect"}
          </LoadingButton>
        </div>
      </div>
    </div>
  );
};
